/**
 * helpers/dateFormat.ts — date / relative-time helpers.
 *
 * `dayjs` is used for the heavy lifting (cheaper than moment, smaller bundle).
 * Format strings match the TNSC reference where they overlap so dashboards
 * read consistently across the suite.
 */

import dayjs from 'dayjs';

/** "10 Apr 2026" — used for the topbar date chip and table cells. */
export function formatDate(d: string | Date | null | undefined): string {
	if (!d) return '—';
	const m = dayjs(d);
	return m.isValid() ? m.format('DD MMM YYYY') : '—';
}

/** "10 Apr 2026 14:32" — for tblDataLoad timestamps. */
export function formatDateTime(d: string | Date | null | undefined): string {
	if (!d) return '—';
	const m = dayjs(d);
	return m.isValid() ? m.format('DD MMM YYYY HH:mm') : '—';
}

/**
 * "2 days ago" / "3 hours ago" / "just now". We keep this dependency-free
 * (no dayjs/relativeTime plugin) so the bundle stays tiny.
 */
export function formatRelative(d: string | Date | null | undefined): string {
	if (!d) return 'never';
	const m = dayjs(d);
	if (!m.isValid()) return '—';
	const diffSec = dayjs().diff(m, 'second');
	if (diffSec < 60) return 'just now';
	const diffMin = Math.floor(diffSec / 60);
	if (diffMin < 60) return `${diffMin} min ago`;
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return `${diffHr} hr ago`;
	const diffDay = Math.floor(diffHr / 24);
	if (diffDay === 1) return '1 day ago';
	if (diffDay < 30) return `${diffDay} days ago`;
	const diffMo = Math.floor(diffDay / 30);
	if (diffMo === 1) return '1 month ago';
	if (diffMo < 12) return `${diffMo} months ago`;
	const diffYr = Math.floor(diffMo / 12);
	return diffYr === 1 ? '1 year ago' : `${diffYr} years ago`;
}

/** "DD MMM" short form for axis labels. */
export function shortDay(d: string | Date): string {
	const m = dayjs(d);
	return m.isValid() ? m.format('DD MMM') : String(d);
}
